import { Fragment, useState } from 'react'
import { useCancelIdmRequest, useConfirmIdmRequest, useIdmAuditTrail } from '../hooks/useIdmRequests'
import type { AuditEvent, IdmRequestRecord } from '../hooks/useIdmRequests'
import { useFilteredQuery } from '@/shared/hooks/useFilteredQuery'
import { FilterBar } from '@/shared/components/molecules/FilterBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { Button } from '@/shared/components/atoms/ui/button'
import { Badge } from '@/shared/components/atoms/ui/badge'

const STATUS_COLORS: Record<string, string> = {
	pending_confirmation: 'bg-blue-100 text-blue-800',
	executing: 'bg-orange-100 text-orange-800',
	completed: 'bg-green-100 text-green-800',
	partial: 'bg-yellow-100 text-yellow-800',
	failed: 'bg-red-100 text-red-800',
	expired: 'bg-muted text-muted-foreground',
	cancelled: 'bg-muted text-muted-foreground',
}

function AuditTrail({ requestId }: { requestId: string }) {
	const { data: events = [], isLoading } = useIdmAuditTrail(requestId)

	if (isLoading) return <p className="text-sm text-muted-foreground">Loading audit trail...</p>
	if (events.length === 0) return <p className="text-sm text-muted-foreground">No audit events recorded yet.</p>

	return (
		<div className="space-y-2">
			{events.map((evt: AuditEvent) => (
				<div key={evt.eventId} className="flex items-start gap-3 text-sm">
					<div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2">
							<span className="font-mono text-xs text-muted-foreground">
								{new Date(evt.timestamp).toLocaleString()}
							</span>
							<Badge variant="outline" className="text-[10px] h-4">
								{evt.source}
							</Badge>
						</div>
						<div className="text-foreground">
							<span className="font-medium">{evt.entity}</span>
							<span className="text-muted-foreground mx-1">.</span>
							<span>{evt.action}</span>
						</div>
						{evt.data && Object.keys(evt.data).length > 0 && (
							<details className="mt-1">
								<summary className="text-xs text-muted-foreground cursor-pointer hover:text-muted-foreground">
									payload
								</summary>
								<pre className="text-[10px] bg-white border rounded p-1.5 mt-1 overflow-auto max-h-32">
									{JSON.stringify(evt.data, null, 2)}
								</pre>
							</details>
						)}
					</div>
				</div>
			))}
		</div>
	)
}

const DEFAULT_STATUS_FILTER = 'pending_confirmation,executing,completed,partial,failed'

const IDM_FILTERS = [
	{
		key: 'status',
		label: 'Status',
		type: 'select' as const,
		options: [
			{ label: 'Active (excl. cancelled/expired)', value: DEFAULT_STATUS_FILTER },
			{ label: 'Pending Confirmation', value: 'pending_confirmation' },
			{ label: 'Executing', value: 'executing' },
			{ label: 'Completed', value: 'completed' },
			{ label: 'Partial', value: 'partial' },
			{ label: 'Failed', value: 'failed' },
			{ label: 'Expired', value: 'expired' },
			{ label: 'Cancelled', value: 'cancelled' },
		],
	},
	{
		key: 'action',
		label: 'Action',
		type: 'select' as const,
		options: [
			{ label: 'Create', value: 'create' },
			{ label: 'Modify', value: 'modify' },
			{ label: 'Delete', value: 'delete' },
		],
	},
	{ key: 'email', label: 'Email', type: 'text' as const },
	{ key: 'createdFrom', label: 'From', type: 'date' as const },
	{ key: 'createdTo', label: 'To', type: 'date' as const },
]

export function IdmRequestsPage() {
	const {
		data: requests,
		isLoading,
		error,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<IdmRequestRecord>({
		endpoint: '/employees/idm-requests',
		queryKey: 'idm-requests',
		defaultFilters: { status: DEFAULT_STATUS_FILTER },
	})
	const confirmMutation = useConfirmIdmRequest()
	const cancelMutation = useCancelIdmRequest()
	const [expandedId, setExpandedId] = useState<string | null>(null)

	const handleConfirm = async (requestId: string) => {
		if (!confirm('Confirm this IDM request for execution?')) return
		await confirmMutation.mutateAsync({ requestId })
	}

	const handleCancel = async (requestId: string) => {
		if (!confirm('Cancel this IDM request?')) return
		await cancelMutation.mutateAsync(requestId)
	}

	if (isLoading) return <div className="text-center py-8">Loading IDM requests...</div>
	if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>

	return (
		<div>
			<h1 className="text-2xl font-bold text-foreground mb-4">IDM Requests</h1>
			<FilterBar
				filters={IDM_FILTERS}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="border rounded-lg">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Employee</TableHead>
							<TableHead>Action</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Signals</TableHead>
							<TableHead>Created</TableHead>
							<TableHead>Expires</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{requests.map((req) => {
							const redCount = req.signals?.filter((s) => s.severity === 'red').length ?? 0
							const yellowCount = req.signals?.filter((s) => s.severity === 'yellow').length ?? 0
							const isExpanded = expandedId === req.requestId

							return (
								<Fragment key={req.requestId}>
									<TableRow className="cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : req.requestId)}>
										<TableCell className="font-medium">{req.email}</TableCell>
										<TableCell>
											<Badge variant="outline">{req.action}</Badge>
										</TableCell>
										<TableCell>
											<span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[req.status] ?? 'bg-muted'}`}>
												{req.status.replace(/_/g, ' ')}
											</span>
										</TableCell>
										<TableCell>
											{redCount > 0 && <span className="text-red-600 font-bold mr-2">{redCount} red</span>}
											{yellowCount > 0 && <span className="text-yellow-600">{yellowCount} yellow</span>}
											{redCount === 0 && yellowCount === 0 && <span className="text-muted-foreground">none</span>}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{new Date(req.createdAt).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{new Date(req.expiresAt).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-right">
											{req.status === 'pending_confirmation' && (
												<div className="flex items-center justify-end gap-1">
													<Button
														onClick={(e) => {
															e.stopPropagation()
															void handleConfirm(req.requestId)
														}}
														size="sm"
														className="h-7 px-2 text-xs"
														disabled={confirmMutation.isPending}
													>
														Confirm
													</Button>
													<Button
														onClick={(e) => {
															e.stopPropagation()
															void handleCancel(req.requestId)
														}}
														size="sm"
														variant="destructive"
														className="h-7 px-2 text-xs"
														disabled={cancelMutation.isPending}
													>
														Cancel
													</Button>
												</div>
											)}
										</TableCell>
									</TableRow>
									{isExpanded && (
										<TableRow key={`${req.requestId}-detail`}>
											<TableCell colSpan={7} className="bg-muted p-4">
												<div className="space-y-3">
													<div>
														<h4 className="font-semibold text-sm mb-1">Signals</h4>
														{req.signals?.length ? (
															<ul className="text-sm space-y-1">
																{req.signals.map((s, i) => (
																	<li key={i} className={s.severity === 'red' ? 'text-red-700' : 'text-yellow-700'}>
																		<strong>[{s.severity.toUpperCase()}]</strong> {s.code}: {s.message}
																	</li>
																))}
															</ul>
														) : (
															<p className="text-sm text-muted-foreground">No signals</p>
														)}
													</div>
													{req.confirmationPayload && (
														<div>
															<h4 className="font-semibold text-sm mb-1">Confirmation Details</h4>
															<pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64">
																{JSON.stringify(req.confirmationPayload, null, 2)}
															</pre>
														</div>
													)}
													{req.executionResult && (
														<div>
															<h4 className="font-semibold text-sm mb-1">Execution Result</h4>
															<pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64">
																{JSON.stringify(req.executionResult, null, 2)}
															</pre>
														</div>
													)}
													<div>
														<h4 className="font-semibold text-sm mb-2">Audit Trail</h4>
														<AuditTrail requestId={req.requestId} />
													</div>
												</div>
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							)
						})}
						{requests.length === 0 && (
							<TableRow>
								<TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
									No IDM requests found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
